export const SURF_TIMEZONE = 'Asia/Makassar'
export const FORECAST_REVALIDATE_SECONDS = 1800
export const SURF_SPOTS = Object.freeze([
  { slug: 'selong-belanak-beach', name: 'Selong Belanak Beach', latitude: -8.8730351, longitude: 116.1624384 },
  { slug: 'mawi-beach', name: 'Mawi Beach', latitude: -8.8833321, longitude: 116.1599634 },
  { slug: 'serangan-beach', name: 'Serangan Beach', latitude: -8.8690049, longitude: 116.1415999 },
])

// Fixed fields and coordinates: callers cannot turn this into a general API proxy.
export const MARINE_FIELDS = {
  wave_height: 'm', wave_direction: '°', wave_period: 's',
  swell_wave_height: 'm', swell_wave_direction: '°', swell_wave_period: 's',
  sea_surface_temperature: '°C',
  secondary_swell_wave_height: 'm', secondary_swell_wave_direction: '°', secondary_swell_wave_period: 's',
}
export const WEATHER_FIELDS = {
  wind_speed_10m: 'kn', wind_direction_10m: '°', wind_gusts_10m: 'kn',
  temperature_2m: '°C', weather_code: 'wmo code', precipitation_probability: '%',
}

export function buildForecastUrls() {
  const common = {
    latitude: SURF_SPOTS.map(spot => spot.latitude).join(','),
    longitude: SURF_SPOTS.map(spot => spot.longitude).join(','),
    forecast_days: '7', timezone: SURF_TIMEZONE, timeformat: 'unixtime',
  }
  return {
    marine: `https://marine-api.open-meteo.com/v1/marine?${new URLSearchParams({ ...common, hourly: Object.keys(MARINE_FIELDS).join(','), cell_selection: 'sea' })}`,
    weather: `https://api.open-meteo.com/v1/forecast?${new URLSearchParams({ ...common, hourly: Object.keys(WEATHER_FIELDS).join(','), wind_speed_unit: 'kn', cell_selection: 'land' })}`,
  }
}

const number = value => typeof value === 'number' && Number.isFinite(value)
const validValue = (value, field) => {
  if (!number(value)) return null
  if (field.endsWith('_direction') || field === 'wind_direction_10m') return value >= 0 && value <= 360 ? value % 360 : null
  if (field === 'temperature_2m' || field === 'sea_surface_temperature') return value >= -100 && value <= 100 ? value : null
  if (field === 'precipitation_probability') return value >= 0 && value <= 100 ? value : null
  if (field === 'weather_code') return Number.isInteger(value) && value >= 0 && value <= 99 ? value : null
  return value >= 0 ? value : null
}

export function normalizeFeed(feed, fields) {
  if (!feed || feed.timezone !== SURF_TIMEZONE || feed.utc_offset_seconds !== 28800 || feed.hourly_units?.time !== 'unixtime') throw new Error('Unexpected forecast time basis')
  if (!Array.isArray(feed.hourly?.time) || !feed.hourly.time.length || feed.hourly.time.length > 192) throw new Error('Missing forecast hours')
  for (const [field, unit] of Object.entries(fields)) {
    if (feed.hourly_units?.[field] && feed.hourly_units[field] !== unit) throw new Error(`Unexpected forecast unit: ${field}`)
  }
  const rows = new Map()
  feed.hourly.time.forEach((time, index) => {
    if (!number(time) || !Number.isInteger(time) || time <= 0 || time % 3600 !== 0 || rows.has(time)) throw new Error('Invalid forecast timestamp')
    rows.set(time, Object.fromEntries(Object.keys(fields).map(field => [field,
      feed.hourly_units?.[field] === fields[field] ? validValue(feed.hourly[field]?.[index], field) : null,
    ])))
  })
  return {
    rows,
    grid: { latitude: number(feed.latitude) ? feed.latitude : null, longitude: number(feed.longitude) ? feed.longitude : null, elevation: number(feed.elevation) ? feed.elevation : null },
  }
}

export function normalizeRegionalForecast({ marine, weather, retrievedAt }) {
  if (!Array.isArray(marine) || !Array.isArray(weather) || marine.length !== SURF_SPOTS.length || weather.length !== SURF_SPOTS.length) throw new Error('Unexpected forecast locations')
  const allFields = [...Object.keys(MARINE_FIELDS), ...Object.keys(WEATHER_FIELDS)]
  const spots = SURF_SPOTS.map((spot, index) => {
    const sea = normalizeFeed(marine[index], MARINE_FIELDS)
    const land = normalizeFeed(weather[index], WEATHER_FIELDS)
    const times = [...new Set([...sea.rows.keys(), ...land.rows.keys()])].sort((a, b) => a - b)
    const hours = times.map(time => ({ time, ...Object.fromEntries(allFields.map(field => [field, sea.rows.get(time)?.[field] ?? land.rows.get(time)?.[field] ?? null])) }))
    if (!hours.some(hour => hour.wave_height !== null || hour.wind_speed_10m !== null)) throw new Error('Forecast has no wave or wind data')
    return { ...spot, hours, marineGrid: sea.grid, weatherGrid: land.grid, missingValues: hours.reduce((sum, hour) => sum + allFields.filter(field => hour[field] === null).length, 0) }
  })
  const sameGrid = key => spots.every(spot => number(spot[key].latitude) && number(spot[key].longitude) && spot[key].latitude === spots[0][key].latitude && spot[key].longitude === spots[0][key].longitude)
  return { retrievedAt: new Date(retrievedAt).toISOString(), timezone: SURF_TIMEZONE, provider: 'Open-Meteo', sharedMarineGrid: sameGrid('marineGrid'), sharedWeatherGrid: sameGrid('weatherGrid'), spots }
}

export function localDateKey(time) {
  if (!number(time)) return ''
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: SURF_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(time * 1000))
  const get = type => parts.find(part => part.type === type)?.value
  return `${get('year')}-${get('month')}-${get('day')}`
}
export const timeLabel = time => number(time) ? new Intl.DateTimeFormat('en-GB', { timeZone: SURF_TIMEZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(time * 1000)) : '—'
export const dateLabel = (date, options = { weekday: 'short', day: 'numeric', month: 'short' }) => new Intl.DateTimeFormat('en-GB', { timeZone: SURF_TIMEZONE, ...options }).format(new Date(`${date}T12:00:00+08:00`))
export const formatValue = (value, digits = 1) => number(value) ? value.toFixed(digits) : '—'
export function compassDirection(value) {
  if (!number(value)) return '—'
  return ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'][Math.round(((value % 360 + 360) % 360) / 22.5) % 16]
}
export const currentForecastHour = (hours, now) => hours.find(hour => hour.time === Math.floor(now / 3600000) * 3600) || null
export const isForecastStale = (retrievedAt, now) => !Number.isFinite(Date.parse(retrievedAt)) || now - Date.parse(retrievedAt) > 3 * 3600000
const range = values => {
  const valid = values.filter(number)
  return valid.length ? { min: Math.min(...valid), max: Math.max(...valid) } : null
}
export function forecastDays(hours) {
  const days = new Map()
  for (const hour of hours) {
    const date = localDateKey(hour.time)
    if (!days.has(date)) days.set(date, [])
    days.get(date).push(hour)
  }
  return [...days.entries()].map(([date, rows]) => ({ date, hours: rows, waves: range(rows.map(row => row.wave_height)), wind: range(rows.map(row => row.wind_speed_10m)), swellPeriod: range(rows.map(row => row.swell_wave_period)) }))
}
export function weatherDescription(code) {
  if (!number(code)) return 'Weather unavailable'
  if (code === 0) return 'Clear sky'
  if (code === 1) return 'Mainly clear'
  if (code === 2) return 'Partly cloudy'
  if (code === 3) return 'Overcast'
  if ([45, 48].includes(code)) return 'Fog'
  if ([51, 53, 55, 56, 57].includes(code)) return 'Drizzle'
  if ([61, 63, 65, 66, 67].includes(code)) return 'Rain'
  if ([71, 73, 75, 77, 85, 86].includes(code)) return 'Snow'
  if ([80, 81, 82].includes(code)) return 'Rain showers'
  if ([95, 96, 99].includes(code)) return 'Thunderstorm'
  return 'Weather unavailable'
}
export function forecastContent(spot, overview = false) {
  return { content_id: `selong_guide:page:surf-forecast:${overview ? 'overview' : spot.slug}`, content_type: 'page', content_title: overview ? 'Surf forecast around Selong Belanak' : `${spot.name} surf forecast`, content_slug: overview ? 'surf-forecast' : `surf-forecast/${spot.slug}`, analytics: { pageType: 'practical_guide', primaryTopic: 'surfing', intentClass: 'surf_research', funnelStage: 'explore', searchIntent: 'informational' } }
}
